import classNames from 'clsx';
import React, { memo, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { FOOTER, IS_MY_TEAM, PUBLIC_SHOVE } from 'reactApp/appHelpers/configHelpers';
import { antivirusConfig } from 'reactApp/appHelpers/featuresHelpers/features/antivirusBrand';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import type { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { sendDwh } from 'reactApp/utils/ga';
import { ON_PREMISE } from 'server/constants/environment';

import styles from './Footer.css';

interface IProps {
    storage: EStorageType;
    isAbsolutePos?: boolean;
    withMargins?: boolean;
    bottom?: number;
    right?: number;
}

const ANTIVIRUS_LOGO_SELECTOR = `#footer a[href="${antivirusConfig.href}"]`;

export const Footer = memo(({ storage, isAbsolutePos = false, withMargins = false, bottom, right }: IProps) => {
    const { isAllDocuments, isEmbedded, isIntegration, isInlineIntegration, isIncomingPublic } = getStorage(storage);
    const isB2BUser = useSelector(UserSelectors.isB2BUser);

    // На брендировании показыается старый футер (рендер в SSR)
    const showFooter =
        !isAllDocuments &&
        !isIncomingPublic &&
        !ON_PREMISE &&
        !IS_MY_TEAM &&
        !PUBLIC_SHOVE &&
        !isEmbedded &&
        !isIntegration &&
        !isInlineIntegration;

    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!showFooter || !antivirusConfig.show) {
            return;
        }

        let shown = false;
        const onShow = (entries: IntersectionObserverEntry[]) => {
            if (shown || !entries[0].isIntersecting) {
                return;
            }

            sendDwh({ eventCategory: 'footer', action: 'antivirus_brand_shown', dwhData: { place: storage } });
            shown = true;
        };

        // eslint-disable-next-line compat/compat
        const observer = new IntersectionObserver(onShow, { threshold: 1 });

        const antivirusLogo = ref.current?.querySelector(ANTIVIRUS_LOGO_SELECTOR);
        if (!antivirusLogo) {
            return;
        }

        observer.observe(antivirusLogo);

        return () => {
            observer.disconnect();
        };
    }, [showFooter, storage]);

    useEffect(() => {
        if (!showFooter || !storage || !antivirusConfig.show) {
            return;
        }

        const onClick = () => {
            sendDwh({ eventCategory: 'footer', action: 'antivirus_brand_clicked', dwhData: { place: storage } });
        };

        const antivirusLogo = ref.current?.querySelector(ANTIVIRUS_LOGO_SELECTOR);
        antivirusLogo?.addEventListener('click', onClick);

        return () => {
            antivirusLogo?.removeEventListener('click', onClick);
        };
    }, [storage, showFooter]);

    if (!showFooter) {
        return null;
    }

    const style: Record<string, string> = {};

    if (typeof bottom === 'number') {
        style.bottom = `${bottom}px`;
    }

    if (typeof right === 'number') {
        style.right = `${right}px`;
    }

    return (
        <div
            style={style}
            ref={ref}
            className={classNames(styles.root, {
                [styles.root_absolute]: isAbsolutePos,
                [styles.root_withMargins]: withMargins,
                [styles.root_hiddenLeftCols]: isB2BUser,
            })}
            dangerouslySetInnerHTML={{ __html: FOOTER }}
        />
    );
});

Footer.displayName = 'Footer';
